import { EnhancedUnifiedFormData } from '@/validation/enhancedUnifiedSchemas';
import { calculateAnnualIncome } from '@/utils/unified/dataTransformers';

export interface LoanProgramRecommendation {
  programType: 'conventional' | 'fha' | 'va' | 'usda';
  confidenceScore: number;
  monthlyPayment: number;
  downPaymentRequired: number;
  totalCostOverLife: number;
  interestRate: number;
  pros: string[];
  cons: string[];
  idealFor: string[];
  requirements: string[];
  timeline: string;
  complexity: 'simple' | 'moderate' | 'complex';
}

export const generateLoanProgramRecommendations = (
  formData: EnhancedUnifiedFormData,
  baseMonthlyPayment: number
): LoanProgramRecommendation[] => {
  const recommendations: LoanProgramRecommendation[] = [];
  const creditScore = formData.personalDetails.creditScore;
  const downPaymentPercent = formData.propertyLoan.downPaymentPercentage;
  const homePrice = formData.propertyLoan.homePrice;
  const monthlyIncome = calculateAnnualIncome(formData) / 12;
  
  const studentLoanPayment = (formData.debts?.studentLoanBalance || 0) * 0.01; // Estimate 1% of balance
  const totalMonthlyDebt = 
    (formData.debts?.autoLoanPayment || 0) +
    (formData.debts?.creditCardPayment || 0) +
    studentLoanPayment +
    (formData.debts?.personalLoanPayment || 0) +
    (formData.debts?.childSupportPayment || 0) +
    (formData.debts?.otherDebts || 0);
  
  const dtiRatio = ((totalMonthlyDebt + baseMonthlyPayment) / monthlyIncome) * 100;

  // CONVENTIONAL
  if (creditScore >= 620) {
    const score = calculateConventionalScore(creditScore, downPaymentPercent, dtiRatio);
    const downPayment = homePrice * (downPaymentPercent / 100);
    const interestRate = getConventionalRate(creditScore);
    
    recommendations.push({
      programType: 'conventional',
      confidenceScore: score,
      monthlyPayment: baseMonthlyPayment,
      downPaymentRequired: downPayment,
      totalCostOverLife: baseMonthlyPayment * 360,
      interestRate,
      pros: [
        creditScore >= 740 ? 'Excellent credit score qualifies for best rates' : 'Good credit score acceptable',
        downPaymentPercent >= 20 ? 'No PMI required with 20%+ down payment' : 'PMI can be removed at 20% equity',
        'Flexible property types allowed',
        'Higher loan limits than FHA ($766,550 in most areas)',
        'Fewer restrictions on seller concessions'
      ],
      cons: [
        creditScore < 680 ? 'Credit score below 680 may result in higher rates' : '',
        downPaymentPercent < 20 ? 'PMI required until 20% equity reached' : '',
        dtiRatio > 43 ? 'DTI above 43% may require compensating factors' : '',
        'Stricter income documentation requirements'
      ].filter(Boolean),
      idealFor: [
        'Buyers with strong credit (680+)',
        'Those with 5%+ down payment saved',
        downPaymentPercent >= 20 ? 'Buyers seeking to avoid PMI' : 'Buyers planning to build equity quickly',
        'Properties above FHA loan limits'
      ],
      requirements: [
        'Credit score: 620+ (680+ for best rates)',
        'Down payment: 3-5% minimum (20% to avoid PMI)',
        'DTI ratio: Typically under 43%',
        'Steady employment history (2 years)',
        'Property must be in acceptable condition'
      ],
      timeline: '30-45 days',
      complexity: 'moderate'
    });
  }

  // FHA
  if (creditScore >= 500) {
    const score = calculateFHAScore(creditScore, downPaymentPercent, dtiRatio);
    const fhaDownPayment = homePrice * 0.035;
    const fhaRate = getConventionalRate(creditScore) + 0.25;
    
    recommendations.push({
      programType: 'fha',
      confidenceScore: score,
      monthlyPayment: baseMonthlyPayment * 1.05,
      downPaymentRequired: fhaDownPayment,
      totalCostOverLife: baseMonthlyPayment * 1.05 * 360,
      interestRate: fhaRate,
      pros: [
        'Low down payment (3.5% with 580+ credit)',
        creditScore < 680 ? 'More forgiving credit requirements' : 'Accessible credit requirements',
        'Higher DTI ratios accepted (up to 50% with compensating factors)',
        'Gift funds allowed for down payment',
        'Excellent for first-time homebuyers',
        'Assumable loans (buyer takes over your rate)'
      ],
      cons: [
        'Upfront mortgage insurance premium (1.75% of loan)',
        'Monthly PMI for life of loan if < 10% down',
        'Property must meet strict FHA standards',
        'Loan limits lower than conventional ($472,030 in most areas)',
        'Higher overall insurance costs'
      ],
      idealFor: [
        creditScore < 680 ? 'Buyers with credit scores 580-680' : 'Buyers seeking low down payment options',
        'First-time homebuyers with limited savings',
        'Buyers with DTI ratios 43-50%',
        'Those accepting gift funds for down payment'
      ],
      requirements: [
        'Credit score: 500+ (580+ for 3.5% down)',
        'Down payment: 3.5% minimum (10% for 500-579 credit)',
        'DTI ratio: Up to 50% with compensating factors',
        'Property must pass FHA appraisal',
        'Steady income documentation'
      ],
      timeline: '30-45 days',
      complexity: 'simple'
    });
  }

  // VA (placeholder - would check veteran status)
  const vaScore = 85;
  recommendations.push({
    programType: 'va',
    confidenceScore: vaScore,
    monthlyPayment: baseMonthlyPayment * 0.95,
    downPaymentRequired: 0,
    totalCostOverLife: baseMonthlyPayment * 0.95 * 360,
    interestRate: getConventionalRate(creditScore) - 0.25,
    pros: [
      'NO down payment required',
      'NO monthly PMI/MIP',
      'Competitive interest rates (often lowest available)',
      'No maximum loan amount (subject to entitlement)',
      'Seller can pay all closing costs',
      'Assumable loans'
    ],
    cons: [
      'One-time funding fee (2.15-3.3% of loan)',
      'Property must meet VA standards',
      'VA appraisal required',
      'Limited to primary residence only'
    ],
    idealFor: [
      'All eligible veterans and active duty',
      'Buyers with limited down payment savings',
      'First-time VA loan users',
      'Those seeking lowest monthly payment'
    ],
    requirements: [
      'Valid Certificate of Eligibility (COE)',
      'Credit score: Typically 620+ (lender-specific)',
      'Proof of military service',
      'Sufficient residual income',
      'Property meets VA minimum standards'
    ],
    timeline: '30-50 days',
    complexity: 'moderate'
  });

  // USDA
  const usdaScore = 70;
  recommendations.push({
    programType: 'usda',
    confidenceScore: usdaScore,
    monthlyPayment: baseMonthlyPayment * 0.98,
    downPaymentRequired: 0,
    totalCostOverLife: baseMonthlyPayment * 0.98 * 360,
    interestRate: getConventionalRate(creditScore),
    pros: [
      'NO down payment required',
      'Low mortgage insurance (0.35% annually)',
      'Competitive interest rates',
      'Income limits favor low-to-moderate income buyers',
      'Seller can pay closing costs'
    ],
    cons: [
      'Geographic restrictions (rural areas only)',
      'Income limits apply (typically 115% of AMI)',
      'Property must be in USDA-eligible area',
      'Upfront guarantee fee (1% of loan)',
      'Longer processing times'
    ],
    idealFor: [
      'Buyers in rural/suburban areas',
      'Low-to-moderate income households',
      'Those with limited down payment funds',
      'First-time homebuyers in eligible areas'
    ],
    requirements: [
      'Property in USDA-eligible rural area',
      'Income below area median limits',
      'Credit score: 640+ recommended',
      'U.S. citizenship or permanent residency',
      'Primary residence only'
    ],
    timeline: '45-60 days',
    complexity: 'complex'
  });

  return recommendations.sort((a, b) => b.confidenceScore - a.confidenceScore);
};

const calculateConventionalScore = (creditScore: number, downPayment: number, dti: number): number => {
  let score = 50;
  
  if (creditScore >= 780) score += 30;
  else if (creditScore >= 740) score += 25;
  else if (creditScore >= 700) score += 20;
  else if (creditScore >= 680) score += 15;
  else if (creditScore >= 640) score += 10;
  else if (creditScore >= 620) score += 5;
  
  if (downPayment >= 20) score += 25;
  else if (downPayment >= 15) score += 20;
  else if (downPayment >= 10) score += 15;
  else if (downPayment >= 5) score += 10;
  else if (downPayment >= 3) score += 5;
  
  if (dti <= 28) score += 20;
  else if (dti <= 36) score += 15;
  else if (dti <= 43) score += 10;
  else if (dti <= 50) score += 5;
  else score -= 10;
  
  return Math.min(100, Math.max(0, score));
};

const calculateFHAScore = (creditScore: number, downPayment: number, dti: number): number => {
  let score = 60;
  
  if (creditScore >= 700) score += 15;
  else if (creditScore >= 640) score += 20;
  else if (creditScore >= 580) score += 25;
  else if (creditScore >= 500) score += 15;
  
  if (downPayment >= 10) score += 15;
  else if (downPayment >= 3.5) score += 20;
  
  if (dti <= 43) score += 15;
  else if (dti <= 50) score += 10;
  
  return Math.min(100, Math.max(0, score));
};

const getConventionalRate = (creditScore: number): number => {
  if (creditScore >= 780) return 6.0;
  if (creditScore >= 740) return 6.25;
  if (creditScore >= 700) return 6.5;
  if (creditScore >= 680) return 6.75;
  if (creditScore >= 640) return 7.0;
  if (creditScore >= 580) return 7.5;
  return 8.0;
};
